'use client';

/**
 * Products Browse Section
 *
 * @component_role Explorer section type `products_browse`
 * @business_rule Collapsible product catalogue browser with source filter
 *   (All / Catalog / Custom / Vendor). Independent of the search composer —
 *   has its own expand state, filter state and data fetch via the
 *   product_browse BFF strategy (lazy-loaded on first expand). Hover
 *   reveals an Add to Cart action per row. Used in discovery mode.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery
 */

import { useState } from 'react';
import { ChevronRight, Loader2, Package, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { useStrategyData } from '@/lib/api/hooks';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import type {
  ExplorerSectionConfig,
  LabelsDict,
  ProductBrowseResponse,
  ProductBrowseItem,
} from '@/lib/api/types';

// ============================================================
// Filter Chip
// ============================================================
function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2 py-0.5 text-xs rounded-full border transition-colors',
        active
          ? 'bg-primary text-primary-foreground border-primary'
          : 'bg-transparent text-muted-foreground border-border hover:border-foreground/30',
      )}
    >
      {label}
      {count != null && <span className="ml-1 opacity-70">({count})</span>}
    </button>
  );
}

// ============================================================
// Source Badge (type indicator on each product row)
// ============================================================
const SOURCE_BADGE_STYLES: Record<string, string> = {
  Catalog: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  Custom: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  Vendor: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
};

function SourceBadge({ label }: { label: string }) {
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 text-[10px] font-medium rounded',
        SOURCE_BADGE_STYLES[label] ?? 'bg-muted text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}

// ============================================================
// Main Section Component
// ============================================================
export function ProductsBrowseSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<'all' | 'catalog' | 'custom' | 'vendor'>('all');
  const addItem = useCartStore((s) => s.addItem);
  const browseStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'browse'));

  const { data, isLoading } = useStrategyData<ProductBrowseResponse>(
    browseStrategy,
    { product_type: filter },
    expanded, // only fetch when expanded
  );

  const counts = data?.data?.counts;
  const products = data?.data?.products ?? [];

  // Labels not loaded yet
  if (!L.products_label) return null;

  const handleAddToCart = (p: ProductBrowseItem) => {
    addItem({
      product_sid: p.semantic_id,
      name: p.name,
      quantity: 1,
      vendor_id: p.vendor_sid ?? '',
      vendor_name: p.vendor_name ?? '',
      list_price: p.price,
      image_url: p.image_url,
    });
  };

  /** Display price — catalog has list_price, buyer wishes have indicative range. */
  const priceLabel = (p: ProductBrowseItem): string | null => {
    if (p.price != null) return `$${p.price.toLocaleString()}`;
    if (p.indicative_price_min != null && p.indicative_price_max != null)
      return `$${p.indicative_price_min.toLocaleString()} – $${p.indicative_price_max.toLocaleString()}`;
    if (p.indicative_price_min != null) return `from $${p.indicative_price_min.toLocaleString()}`;
    return null;
  };

  return (
    <div className="border-b border-border">
      {/* Header — click to toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50 transition-colors"
      >
        <ChevronRight className={cn('w-4 h-4 transition-transform', expanded && 'rotate-90')} />
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="font-medium">{L.products_label}</span>
        {counts && <span className="ml-auto text-xs text-muted-foreground">{counts.all}</span>}
      </button>

      {expanded && (
        <div className="px-3 pb-2">
          {/* Filter pills */}
          <div className="flex flex-wrap gap-1 mb-2">
            <FilterChip
              label={L.products_filter_all}
              count={counts?.all}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            <FilterChip
              label={L.products_filter_catalog}
              count={counts?.catalog}
              active={filter === 'catalog'}
              onClick={() => setFilter('catalog')}
            />
            <FilterChip
              label={L.products_filter_custom}
              count={counts?.custom}
              active={filter === 'custom'}
              onClick={() => setFilter('custom')}
            />
            <FilterChip
              label={L.products_filter_vendor}
              count={counts?.vendor}
              active={filter === 'vendor'}
              onClick={() => setFilter('vendor')}
            />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty */}
          {!isLoading && products.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">{L.products_empty}</p>
          )}

          {/* Product rows */}
          {!isLoading && products.length > 0 && (
            <div className="space-y-0.5 max-h-64 overflow-auto">
              {products.map((p) => (
                <div
                  key={p.semantic_id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent/50 transition-colors group"
                >
                  <SourceBadge label={p.source_label} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{p.name}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {p.category && <span className="capitalize">{p.category}</span>}
                      {priceLabel(p) && <span>{priceLabel(p)}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddToCart(p)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-primary/10 transition-opacity"
                    title={L.products_add_to_cart}
                  >
                    <ShoppingCart className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {data?.data?.has_more && (
            <p className="text-xs text-muted-foreground text-center mt-1">
              {L.products_has_more.replace('{n}', String(products.length))}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
